import { useEffect, useState } from "react";
import { ChartLineUp, Lightning, LinkSimple, CursorClick, CurrencyDollar, Confetti, CurrencyCircleDollar, IdentificationCard } from "@phosphor-icons/react";
import { XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart } from "recharts";
import { analyticsSummary } from "../lib/api";
import { formatMoney } from "../lib/currency";
import BudgetCard from "../components/BudgetCard";

const Stat = ({ label, value, icon: Icon, color = "#ccff00", size = "text-5xl" }) => (
  <div className="bg-[#0a0a0a] border border-white/10 p-6 hover:border-white/20 transition-colors">
    <div className="flex items-center justify-between mb-6">
      <div className="label-mono">{label}</div>
      <Icon size={20} weight="duotone" style={{ color }} />
    </div>
    <div
      className={`font-display ${size} font-black tracking-tighter`}
      style={{ color }}
    >
      {value}
    </div>
  </div>
);

const MultiCurrencyRevenue = ({ entries, usdTotal, displayTotal, displayCcy }) => (
  <span className="flex flex-col leading-tight" data-testid="analytics-revenue-multi">
    {entries.map(([ccy, amt]) => (
      <span key={ccy} className="text-2xl">
        {amt.toFixed(2)}
        <span className="text-[10px] font-mono ml-1 text-zinc-500">{ccy}</span>
      </span>
    ))}
    {typeof displayTotal === "number" && (
      <span
        className="text-[10px] font-mono text-zinc-500 pt-1 border-t border-white/5 mt-1"
        data-testid="analytics-revenue-usd"
      >
        ≈ {formatMoney(displayTotal, displayCcy)}
      </span>
    )}
  </span>
);

const renderRevenue = (data) => {
  const map = data?.total_revenue_by_currency || {};
  const entries = Object.entries(map).filter(([, v]) => v > 0);
  const ccy = data?.display_currency || "USD";
  if (entries.length <= 1) {
    return formatMoney(data?.total_revenue_display ?? data?.total_revenue ?? 0, ccy);
  }
  entries.sort((a, b) => b[1] - a[1]);
  return <MultiCurrencyRevenue entries={entries} usdTotal={data?.total_revenue_usd} displayTotal={data?.total_revenue_display} displayCcy={ccy} />;
};

export default function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    analyticsSummary()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        console.warn("analyticsSummary failed:", err?.message || err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const series = data?.series || [];

  return (
    <div className="max-w-6xl">
      <div className="mb-10">
        <div className="label-mono mb-3">metrics</div>
        <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tighter">
          Analytics <span className="font-black text-[#ccff00]">Overview</span>
        </h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-10" data-testid="stats-grid">
        <Stat
          label="ads generated"
          value={data?.total_ads ?? 0}
          icon={Lightning}
          color="#ccff00"
        />
        <Stat
          label="links tracked"
          value={data?.total_links ?? 0}
          icon={LinkSimple}
          color="#06b6d4"
        />
        <Stat
          label="total clicks"
          value={data?.total_clicks ?? 0}
          icon={CursorClick}
          color="#ff007f"
        />
        <Stat
          label="conversions"
          value={data?.total_conversions ?? 0}
          icon={Confetti}
          color="#7dd3fc"
        />
        <div data-testid="stat-lead-slips">
          <Stat
            label="lead slips"
            value={
              <span className="flex flex-col leading-tight">
                <span>{data?.total_leads ?? 0}</span>
                <span className="text-[10px] font-mono text-emerald-400 mt-1">
                  {data?.qualified_leads ?? 0} qualified
                </span>
              </span>
            }
            icon={IdentificationCard}
            color="#a3e635"
            size="text-4xl"
          />
        </div>
        <Stat
          label="revenue"
          value={renderRevenue(data)}
          icon={CurrencyDollar}
          color="#f472b6"
          size="text-3xl"
        />
      </div>

      {/* LLM cost roll-up */}
      {data?.cost_estimate && (
        <BudgetCard />
      )}
      {data?.cost_estimate_display && (
        <div
          className="bg-[#0a0a0a] border border-white/10 p-6 mb-10 grid grid-cols-1 md:grid-cols-4 gap-6"
          data-testid="workspace-cost-card"
        >
          <div className="md:col-span-1">
            <div className="flex items-center justify-between mb-3">
              <div className="label-mono">llm spend</div>
              <CurrencyCircleDollar size={20} weight="duotone" className="text-amber-400" />
            </div>
            <div className="font-display text-4xl font-black tracking-tighter text-amber-400" data-testid="workspace-cost-total">
              {formatMoney(data.cost_estimate_display.total, data.cost_estimate_display.currency)}
            </div>
            <div className="text-[10px] font-mono text-zinc-500 mt-1">
              across {data.cost_estimate_display.ad_sets} ad sets · estimate
            </div>
          </div>
          <div className="md:col-span-3 grid grid-cols-3 gap-2 text-sm">
            <div className="bg-[#121212] border border-white/5 p-3">
              <div className="label-mono text-[9px]">copy</div>
              <div className="font-display text-xl font-bold text-[#ccff00] mt-1">
                {formatMoney(data.cost_estimate_display.copy, data.cost_estimate_display.currency)}
              </div>
            </div>
            <div className="bg-[#121212] border border-white/5 p-3">
              <div className="label-mono text-[9px]">images</div>
              <div className="font-display text-xl font-bold text-pink-400 mt-1">
                {formatMoney(data.cost_estimate_display.images, data.cost_estimate_display.currency)}
              </div>
            </div>
            <div className="bg-[#121212] border border-white/5 p-3">
              <div className="label-mono text-[9px]">compliance</div>
              <div className="font-display text-xl font-bold text-cyan-300 mt-1">
                {formatMoney(data.cost_estimate_display.compliance, data.cost_estimate_display.currency)}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-[#0a0a0a] border border-white/10 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="label-mono">click volume · last 14 days</div>
              <div className="font-display text-2xl font-bold mt-1">Traffic</div>
            </div>
            <ChartLineUp size={22} className="text-[#ccff00]" weight="duotone" />
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series}>
                <defs>
                  <linearGradient id="clickGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ccff00" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#ccff00" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1f1f1f" strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  stroke="#71717a"
                  fontSize={10}
                  fontFamily="JetBrains Mono"
                />
                <YAxis stroke="#71717a" fontSize={10} fontFamily="JetBrains Mono" />
                <Tooltip
                  contentStyle={{
                    background: "#0a0a0a",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 0,
                    fontFamily: "JetBrains Mono",
                    fontSize: 12,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="clicks"
                  stroke="#ccff00"
                  strokeWidth={2}
                  fill="url(#clickGrad)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[#0a0a0a] border border-white/10 p-6">
          <div className="label-mono mb-4">top links</div>
          {data?.top_links?.length ? (
            <div className="space-y-3">
              {data.top_links.map((l, i) => (
                <div key={l.id} className="flex items-center gap-3">
                  <div className="font-mono text-xs text-zinc-500 w-4">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold truncate">{l.name}</div>
                    <div className="font-mono text-[10px] text-zinc-500 truncate">
                      {l.url}
                    </div>
                  </div>
                  <div className="font-mono font-bold text-[#ccff00]">
                    {l.clicks}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-zinc-500 text-sm">No click data yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
